import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnDestroy, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { ButtonIconProps } from '@yooga-tecnologia/mantra/loader';

import { CurrencyPipe } from '../../../../pipes/currency.pipe';
import { CartService, MenuItemModalData } from '../../../../domain/services/cart.service';
import { MenuItemDto } from '../../../../services/menu/dto/api-response-dto';
import { Table } from '../../../../domain/models/table';
import { TableService } from '../../../../domain/services/table.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-product-modal-default',
  standalone: true,
  imports: [
    CommonModule,
    CurrencyPipe,
  ],
  templateUrl: './product-modal-default.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProductModalDefaultComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() product!: MenuItemDto;

  readonly modalId: string = 'product-modal-default';

  showScrolledHeader: boolean = false;
  dialogBody: HTMLElement | null = null;
  private scrollListener: (() => void) | null = null;
  isOpened: boolean = false;
  productData!: MenuItemModalData | undefined;
  tableSubscription: Subscription;
  quantity!: number;
  isEditMode: boolean = false;
  additionalInfo: string = '';
  moduleBot: boolean = false;
  backButtonProps: ButtonIconProps = {
    icon: 'caret-down',
    color: 'neutral',
    variant: 'regular',
    size: 'medium',
  };

  constructor(
    private cartService: CartService,
    private ngxSmartModalService: NgxSmartModalService,
    private cdr: ChangeDetectorRef,
    private tableService: TableService
  ) {}

  ngOnInit(): void {
    this.productData = this.ngxSmartModalService.getModal(this.modalId)?.getData() as MenuItemModalData;
    this.quantity = this.productData?.quantity || 1;

    // Check if in edit mode
    this.isEditMode = !!this.productData?.cartItemId;

    // Pre-populate additional info if editing
    if (this.isEditMode && this.productData?.additional_info) {
      this.additionalInfo = this.productData.additional_info;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.setupScrollListener();
      this.lockBodyScroll();
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.dialogBody && this.scrollListener) {
      this.dialogBody.removeEventListener('scroll', this.scrollListener);
    }
    this.unlockBodyScroll();
    this.ngxSmartModalService.resetModalData(this.modalId);
  }

  /**
   * Lock body scroll when modal is open (mobile fix)
   */
  private lockBodyScroll(): void {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${this.cartService.savedScrollPosition}px`;
  }

  /**
   * Unlock body scroll when modal is closed (mobile fix)
   */
  private unlockBodyScroll(): void {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';

    window.scrollTo(0, this.cartService.savedScrollPosition);
  }

  private setupScrollListener(): void {
    // Try multiple selectors to find the correct scrollable element
    this.dialogBody = document.querySelector('.nsm-dialog-animation-element.nsm-dialog-open') as HTMLElement;

    if (!this.dialogBody) {
      this.dialogBody = document.querySelector('.dialog-container') as HTMLElement;
    }

    if (!this.dialogBody) {
      this.dialogBody = document.querySelector('.dialog-full-height') as HTMLElement;
    }

    if (this.dialogBody) {
      this.scrollListener = () => {
        const scrollY = this.dialogBody!.scrollTop;
        const shouldShowHeader = scrollY > 200;

        if (this.showScrolledHeader !== shouldShowHeader) {
          this.showScrolledHeader = shouldShowHeader;
          this.cdr.detectChanges();
        }
      };
      this.tableSubscription = this.tableService.$openedTable.subscribe(
        (table: Table | null) => {
          if (table) {
            this.isOpened = table.installation.isOpened;
            this.moduleBot = table.moduleBot || false;
          }
        }
      );
      this.dialogBody.addEventListener('scroll', this.scrollListener, { passive: true });
    }
  }

  closeDialog(): void {
    this.ngxSmartModalService.close(this.modalId);
  }

  onQuantityChange(event: any): void {
    if (event.detail.value > 99) {
      this.quantity = 99;
      return;
    }

    this.quantity = event.detail.value;
  }

  getTotalPrice(): number {
    return this.product.promotional_price ? this.product.promotional_price * this.quantity : this.product.price * this.quantity;
  }

  handleAddToCart(): void {

    if (this.isEditMode && this.productData?.cartItemId) {
      // Update existing cart item
      const total = this.product.promotional_price || this.product.price;
      this.cartService.updateCartItem(
        this.productData.cartItemId,
        this.quantity,
        undefined,
        total,
        this.additionalInfo
      );
    } else {
      // Add new item to cart
      this.cartService.addItemToCart(this.product, this.quantity, undefined, undefined, this.additionalInfo);
    }
  }

  onObservationChange(event: any): void {
    this.additionalInfo = event.target.value;
  }
}
